/**
 * WLS Countdown Timer JavaScript
 * Handles countdown functionality for multiple countdown instances
 */

if (!customElements.get('wlsn-countdown')) {
  class WLSCountdown extends HTMLElement {
    constructor() {
      super();
      
      this.classes = {
        ready: 'is-ready',
        countdownValue: 'wlsn-countdown-value',
        countdownLabel: 'wlsn-countdown-label'
      };

      this.elements = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]')
      };

      this.endDate = null;
      this.interval = null;
      
      this.init();
    }

    /**
     * @description Initialize the countdown
     */
    init() {
      const endDate = this.getAttribute('data-end-date');
      
      if (!endDate) {
        console.warn('WLSCountdown: No end date provided, countdown will not render');
        return;
      }
      
      // Parse custom format and convert to UTC
      this.endDate = this.parseCustomDate(endDate);
      
      if (isNaN(this.endDate)) {
        console.error('WLSCountdown: Invalid end date format');
        return;
      }
      
      this.updateCountdown();
      this.startTimer();
      
      // Make countdown visible after initialization
      this.classList.add(this.classes.ready);
    }

    /**
     * @param {string} dateString
     * @returns {number}
     * @description Parse custom date format (year-month-day:hour:minute:second) and convert to UTC timestamp
     */
    parseCustomDate(dateString) {
      
      // Check if it's our custom format (year-month-day:hour:minute:second)
      if (dateString.includes(':')) {
        // Split by colon first, then handle the date part
        const timeParts = dateString.split(':');
        
        if (timeParts.length === 4) {
          // Format: "2025-09-07:00:00:00"
          const datePart = timeParts[0]; // "2025-09-07"
          const hour = parseInt(timeParts[1], 10);
          const minute = parseInt(timeParts[2], 10);
          const second = parseInt(timeParts[3], 10);
          
          // Split the date part by dashes
          const dateParts = datePart.split('-');
          if (dateParts.length === 3) {
            const year = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10);
            const day = parseInt(dateParts[2], 10);
            
            // Validate date components
            if (!this.validateDateComponents(year, month, day, hour, minute, second)) {
              console.error('WLSCountdown: Invalid date components');
              return NaN;
            }
            
            // Use ISO string format for better Safari compatibility
            const isoString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
            
            // Create date using ISO string for better cross-browser compatibility
            const date = new Date(isoString);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
              console.error('WLSCountdown: Invalid date created from ISO string');
              return NaN;
            }
            
            return date.getTime();
          }
        } else if (timeParts.length === 6) {
          // Legacy format: "2025:09:07:00:00:00" (colon-separated)
          const [year, month, day, hour, minute, second] = timeParts.map(part => parseInt(part, 10));
          
          // Validate date components
          if (!this.validateDateComponents(year, month, day, hour, minute, second)) {
            console.error('WLSCountdown: Invalid date components');
            return NaN;
          }
          
          // Use ISO string format for better Safari compatibility
          const isoString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
          
          // Create date using ISO string for better cross-browser compatibility
          const date = new Date(isoString);
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            console.error('WLSCountdown: Invalid date created from ISO string');
            return NaN;
          }
          
          return date.getTime();
        } else {
          console.log('Wrong number of time parts:', timeParts.length);
        }
      } else {
        console.log('No colon found, trying standard format');
      }
      
      // Fallback to standard ISO format
      const fallbackDate = new Date(dateString);
      
      if (isNaN(fallbackDate.getTime())) {
        console.error('WLSCountdown: Invalid fallback date');
        return NaN;
      }
      return fallbackDate.getTime();
    }

    /**
     * @param {number} year
     * @param {number} month
     * @param {number} day
     * @param {number} hour
     * @param {number} minute
     * @param {number} second
     * @returns {boolean}
     * @description Validate date components are within valid ranges
     */
    validateDateComponents(year, month, day, hour, minute, second) {
      // Check if all components are numbers
      if ([year, month, day, hour, minute, second].some(isNaN)) {
        return false;
      }
      
      // Validate ranges
      if (year < 2020 || year > 2030) return false; // Reasonable year range
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      if (hour < 0 || hour > 23) return false;
      if (minute < 0 || minute > 59) return false;
      if (second < 0 || second > 59) return false;
      
      // Additional validation for specific months
      const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const maxDays = daysInMonth[month - 1];
      
      // Handle leap year for February
      if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) {
        if (day > 29) return false;
      } else {
        if (day > maxDays) return false;
      }
      
      return true;
    }

    /**
     * @description Update the countdown display
     */
    updateCountdown() {
      const now = new Date().getTime();
      const distance = this.endDate - now;
      
      if (distance < 0) {
        // Countdown finished
        this.setTimeValues(0, 0, 0, 0);
        this.stopTimer();
        return;
      }
      
      // Calculate time units
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      this.setTimeValues(days, hours, minutes, seconds);
    }

    /**
     * @param {number} days
     * @param {number} hours
     * @param {number} minutes
     * @param {number} seconds
     * @description Set the time values in the DOM
     */
    setTimeValues(days, hours, minutes, seconds) {
      if (this.elements.days) this.elements.days.textContent = days.toString().padStart(2, '0');
      if (this.elements.hours) this.elements.hours.textContent = hours.toString().padStart(2, '0');
      if (this.elements.minutes) this.elements.minutes.textContent = minutes.toString().padStart(2, '0');
      if (this.elements.seconds) this.elements.seconds.textContent = seconds.toString().padStart(2, '0');
    }

    /**
     * @description Start the countdown timer
     */
    startTimer() {
      this.interval = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    }

    /**
     * @description Stop the countdown timer
     */
    stopTimer() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    /**
     * @description Clean up when element is removed
     */
    disconnectedCallback() {
      this.stopTimer();
    }

    /**
     * @description Destroy the countdown
     */
    destroy() {
      this.stopTimer();
    }
  }
  
  customElements.define('wlsn-countdown', WLSCountdown);
}
